<script lang="ts" setup>
import type { BibliographyEntriesWindowItem } from "@/types/global.ts";

const windowsStore = useWindowsStore();
const { addWindow } = windowsStore;

interface Props {
	params: BibliographyEntriesWindowItem["params"];
}

const props = defineProps<Props>();
const { params } = toRefs(props);

const emit = defineEmits(["updateQueryParam"]);

const { data, isPending, isPlaceholderData } = useBiblioTeiQuery(params);
const openNewWindowFromAnchor = useAnchorClickHandler();

const isLoading = computed(() => {
	return isPending.value || isPlaceholderData.value;
});

const queryString: Ref<string> = ref(params.value.queryString);
const isTextQuery: Ref<boolean> = ref(true);
const isMapQuery: Ref<boolean> = ref(false);

params.value.isQueryVisible ??= false;
const isListOpen = ref(queryString.value !== "");

function submitNewQuery(map: boolean): void {
	if (queryString.value === "") return;
	if (!isTextQuery.value && !isMapQuery.value) isTextQuery.value = true;
	params.value.queryString = queryString.value;
	params.value.showMap = map;
	params.value.isQueryVisible = false;
	emit("updateQueryParam", queryString.value);
	if (map) {
		addWindow({
			targetType: "WMap",
			params: {
				queryString: queryString.value,
				endpoint: "bibl_markers_tei",
			},
			title: `Bibl. Locations for "${queryString.value}"`,
		});
		isListOpen.value = true;
	} else {
		isListOpen.value = true;
	}
}

function submitNewQueryKeyup(event: KeyboardEvent): void {
	if (event.key === "Enter") {
		submitNewQuery(false);
	}
}
</script>

<template>
	<div
		class="relative isolate size-full overflow-auto"
		:class="{ 'opacity-50 grayscale': isLoading }"
	>
		<!-- eslint-disable vuejs-accessibility/form-control-has-label, tailwindcss/no-custom-classname -->
		<Collapsible v-model:open="params.isQueryVisible" class="prose max-w-3xl px-8 pt-8 pb-4">
			<CollapsibleTrigger
				class="flex w-full items-baseline bg-primary p-4 py-0 font-bold text-on-primary"
			>
				<span>Query Bibliography:</span>
				<span v-if="queryString !== ''" class="spQueryText">&nbsp;&nbsp;{{ queryString }}</span>
				<div class="relative top-1 mr-4 ml-auto">
					<div v-if="!params.isQueryVisible">
						<svg class="svg-icon" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M511.5 789.9 80.6 359c-22.8-22.8-22.8-59.8 0-82.6 22.8-22.8 59.8-22.8 82.6 0l348.3 348.3 348.3-348.3c22.8-22.8 59.8-22.8 82.6 0 22.8 22.8 22.8 59.8 0 82.6L511.5 789.9 511.5 789.9zM511.5 789.9"
							/>
						</svg>
					</div>
					<div v-if="params.isQueryVisible">
						<svg class="svg-icon" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M511.5 259.3 942.4 690.1c22.8 22.8 22.8 59.8 0 82.6-22.8 22.8-59.8 22.8-82.6 0L511.5 424.5 163.2 772.8c-22.8 22.8-59.8 22.8-82.6 0-22.8-22.8-22.8-59.8 0-82.6L511.5 259.3 511.5 259.3zM511.5 259.3"
							/>
						</svg>
					</div>
				</div>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<div class="max-w-3xl bg-gray-200 px-4">
					<div class="pt-3">
						<input
							v-model="queryString"
							class="block w-full rounded-sm border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
							placeholder="Search in bibliographies&hellip;"
							type="text"
							@keyup.enter="submitNewQueryKeyup"
						/>
					</div>
					<div class="my-3">
						<button class="biblQueryBtn" :disabled="!queryString" @click="submitNewQuery(false)">
							Show as List
						</button>
					</div>
					<div class="my-3">
						<button class="biblQueryBtn" :disabled="!queryString" @click="submitNewQuery(true)">
							Show on a Map
						</button>
					</div>
					<div class="pb-4">
						<p>
							For details as to how to formulate meaningful queries in the bibliography
							<a
								class="aVicText"
								data-label="Bibliography (Details)"
								data-target-type="Text"
								data-text-id="li_vicavExplanationBibliography"
								href="#"
								@click="openNewWindowFromAnchor"
							>
								click here
							</a>
							<!-- TODO: remove the linebreak before the punctuation so that it sticks to the html end tag and find the linting rule to ignore -->
							.
						</p>
					</div>
				</div>
			</CollapsibleContent>
		</Collapsible>

		<!-- eslint-disable-next-line vue/no-v-html, vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
		<Collapsible v-model:open="isListOpen" class="prose mb-auto max-w-3xl p-8">
			<CollapsibleTrigger class="dvStats flex w-full items-baseline">
				{{ data ? data.stats : "&nbsp;" }}

				<div class="relative top-1 mr-4 ml-auto">
					<div v-if="data && !isListOpen">
						<svg class="svg-icon" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M511.5 789.9 80.6 359c-22.8-22.8-22.8-59.8 0-82.6 22.8-22.8 59.8-22.8 82.6 0l348.3 348.3 348.3-348.3c22.8-22.8 59.8-22.8 82.6 0 22.8 22.8 22.8 59.8 0 82.6L511.5 789.9 511.5 789.9zM511.5 789.9"
							/>
						</svg>
					</div>
					<div v-if="data && isListOpen">
						<svg
							class="svg-icon"
							version="1.1"
							viewBox="0 0 1024 1024"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M511.5 259.3 942.4 690.1c22.8 22.8 22.8 59.8 0 82.6-22.8 22.8-59.8 22.8-82.6 0L511.5 424.5 163.2 772.8c-22.8 22.8-59.8 22.8-82.6 0-22.8-22.8-22.8-59.8 0-82.6L511.5 259.3 511.5 259.3zM511.5 259.3"
							/>
						</svg>
					</div>
				</div>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<!-- eslint-disable vue/no-v-html -->
				<p v-if="data && !data.hasResults">No results.</p>
				<div v-else-if="data" v-html="data.html" />
			</CollapsibleContent>
		</Collapsible>

		<Centered v-if="isLoading">
			<LoadingIndicator />
		</Centered>
	</div>
</template>

<style>
@reference "@/styles/index.css";

svg.svg-icon {
	vertical-align: middle;
	overflow: hidden;
	width: 1em;
	height: 1em;
	fill: currentColor;
}

/* stylelint-disable selector-class-pattern, block-no-empty */
.dvStats {
	@apply mb-1.25 pb-1.25 pl-1.25 border border-solid border-primary bg-primary text-on-primary font-bold;
}

.spQueryText {
	@apply text-gray-200 italic;
}

.dvAuthor {
	@apply block border-t border-dotted border-primary;
}

.dvBiblBlock {
	@apply block ml-5 pb-2.5;
}

.imgBiblItem {
	@apply inline size-8 m-0 border-none pr-1.5 align-middle box-border;
}

.dvBibBook,
.dvBibBookSection,
.dvBibArticle,
.dvThesis {
}

.dvBibArticle .fa-book,
.dvBibBook .fa-file-text,
.dvBibBookSection .fa-file-text {
	@apply block;
}

.biblQueryBtn {
	@apply w-full h-10 text-primary border-2 border-solid border-primary font-bold bg-on-primary inline-block text-center whitespace-nowrap align-middle rounded disabled:border-gray-400 disabled:text-gray-400 hover:disabled:bg-on-primary hover:disabled:text-gray-400;
}

.biblQueryBtn:hover {
	@apply bg-primary text-on-primary;
}
</style>
